import React, { useState } from 'react';
import { Calculator } from '../../types';
import { CalculatorShell } from './CalculatorShell';
import { exportToPDF, shareToWhatsApp } from '../../utils/exportUtils';
import { AllCalculatorsModule } from './AllCalculatorsModule';

// Import individual calculators
import CPFValidator from './devtools/CPFValidator';
import CNPJValidator from './devtools/CNPJValidator';
import TimestampConverter from './devtools/TimestampConverter';
import JSONValidator from './devtools/JSONValidator';
import UUIDGenerator from './devtools/UUIDGenerator';
import BytesConverter from './devtools/BytesConverter';
import PaginationCalculator from './devtools/PaginationCalculator';
import SlugGenerator from './devtools/SlugGenerator';
import QueryStringParser from './devtools/QueryStringParser';
import JSONPayloadSize from './devtools/JSONPayloadSize';
import HashGenerator from './devtools/HashGenerator';
import Base64Encoder from './devtools/Base64Encoder';

interface DevToolsModuleProps {
  calculator: Calculator;
  onBack: () => void;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export const DevToolsModule: React.FC<DevToolsModuleProps> = ({
  calculator,
  onBack,
  isFavorite,
  onToggleFavorite,
}) => {
  // Rolar para o topo ao entrar na calculadora
  React.useEffect(() => {
    window.scrollTo(0, 0);
    const timeoutId = setTimeout(() => {
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [calculator.slug]);

  const [exportData, setExportData] = useState<{ title: string, results: { label: string, value: string }[] } | null>(null);

  const handleExportPDF = () => {
    if (!exportData) return;
    exportToPDF({
      title: exportData.title,
      description: `Relatório gerado em ${new Date().toLocaleDateString('pt-BR')}`,
      results: exportData.results
    });
  };

  const handleShareWhatsApp = () => {
    if (!exportData) return;
    shareToWhatsApp(exportData.title, exportData.results);
  };

  const handleResultChange = React.useCallback((data: { label: string, value: string }[]) => {
    setExportData({ title: calculator.title, results: data });
  }, [calculator.title]);

  const supportedSlugs = [
    'validador-cpf',
    'timestamp-converter',
    'validador-cnpj',
    'validador-json',
    'gerador-uuid',
    'conversor-bytes',
    'calculadora-paginacao',
    'gerador-slug',
    'parser-query-string',
    'json-payload-size',
    'gerador-hash',
    'base64-encoder'
  ];

  if (!supportedSlugs.includes(calculator.slug)) {
    return <AllCalculatorsModule calculator={calculator} onBack={onBack} />;
  }

  const renderDevTool = () => {
    switch (calculator.slug) {
      case 'validador-cpf':
        return <CPFValidator onResultChange={handleResultChange} />;
      case 'timestamp-converter':
        return <TimestampConverter onResultChange={handleResultChange} />;
      case 'validador-cnpj':
        return <CNPJValidator onResultChange={handleResultChange} />;
      case 'validador-json':
        return <JSONValidator onResultChange={handleResultChange} />;
      case 'gerador-uuid':
        return <UUIDGenerator onResultChange={handleResultChange} />;
      case 'conversor-bytes':
        return <BytesConverter onResultChange={handleResultChange} />;
      case 'calculadora-paginacao':
        return <PaginationCalculator onResultChange={handleResultChange} />;
      case 'gerador-slug':
        return <SlugGenerator onResultChange={handleResultChange} />;
      case 'parser-query-string':
        return <QueryStringParser onResultChange={handleResultChange} />;
      case 'json-payload-size':
        return <JSONPayloadSize onResultChange={handleResultChange} />;
      case 'gerador-hash':
        return <HashGenerator onResultChange={handleResultChange} />;
      case 'base64-encoder':
        return <Base64Encoder onResultChange={handleResultChange} />;
      default:
        return null;
    }
  };

  return (
    <CalculatorShell
      calculator={calculator}
      onBack={onBack}
      isFavorite={isFavorite}
      onToggleFavorite={onToggleFavorite}
      onExportPDF={exportData ? handleExportPDF : undefined}
      onShareWhatsApp={exportData ? handleShareWhatsApp : undefined}
    >
      {renderDevTool()}
    </CalculatorShell>
  );
};
